// The Curri design system mapped onto Scalar's CSS variables.
//
// Scalar owns the reference pane's internals, so branding it means overriding its
// variables rather than restyling its DOM. Every value here points at a
// `--curri-*` alias from `tokens.css`, which in turn points at a `@curri/styles`
// semantic token — so this file holds no colour of its own, and a design-system
// change reaches the framed reference and the chrome around it together. (These
// resolve because Scalar mounts into our document, not a shadow root.)
//
// Two things worth knowing:
//   • `--scalar-color-accent` is the *legible* brand token, not the fill. The raw
//     brand green fails text contrast on white; the design system treats it as a
//     fill that carries inverse text, which is what `--scalar-button-1` does.
//   • The base theme is kept (`theme: 'default'`). Setting `theme: 'none'` strips
//     Scalar's own styling and would leave us owning far more CSS than this.
export const scalarCustomCss = `
.light-mode {
  /* Scalar renders *inside* the content sheet, so its page background is the
     sheet (white) and its panels step down from there — rather than the shell's
     grey base, which would look like the nav bleeding into the sheet. */
  --scalar-background-1: var(--curri-sheet);
  --scalar-background-2: var(--curri-canvas);
  --scalar-background-3: var(--color-elevation-surface-sunken);
  --scalar-background-accent: var(--curri-brand-subtle);

  --scalar-color-1: var(--curri-text);
  --scalar-color-2: var(--curri-text-secondary);
  --scalar-color-3: var(--curri-text-tertiary);
  --scalar-color-accent: var(--curri-accent);

  --scalar-border-color: var(--curri-border);

  --scalar-radius: var(--curri-radius-sm);
  --scalar-radius-lg: var(--curri-radius-md);
  --scalar-radius-xl: var(--curri-radius-lg);

  --scalar-button-1: var(--curri-brand-fill);
  --scalar-button-1-color: var(--curri-brand-on-fill);
  --scalar-button-1-hover: var(--curri-brand-fill-hover);

  /* Its endpoint sidebar is a column *within* the sheet, so it stays white and
     leans on its border — a grey column here would read as a third layer. */
  --scalar-sidebar-background-1: var(--curri-sheet);
  --scalar-sidebar-color-1: var(--curri-text);
  --scalar-sidebar-color-2: var(--curri-text-secondary);
  --scalar-sidebar-border-color: var(--curri-border);
  --scalar-sidebar-item-hover-background: var(--curri-canvas);
  --scalar-sidebar-item-active-background: var(--curri-brand-subtle);
  --scalar-sidebar-color-active: var(--curri-accent);
  --scalar-sidebar-search-background: var(--curri-canvas);
  --scalar-sidebar-search-border-color: var(--curri-border);

  /* HTTP method pills, mapped to the design system's status text tokens. */
  --scalar-color-green: var(--color-text-success);
  --scalar-color-blue: var(--color-text-information);
  --scalar-color-orange: var(--color-text-warning);
  --scalar-color-red: var(--color-text-danger);
}

/* Scalar lays itself out full-viewport. Framed, it fills the pane when the
   document is short and grows past it when it is long — min-height, not height,
   because a hard 100% clips the reference at one screen and the rest becomes
   unreachable. The pane itself scrolls (see .curri-scalar-mount). */
.scalar-app,
.scalar-api-reference {
  min-height: 100%;
}

/* Display-weight headings, matching the chrome. */
.scalar-app h1,
.scalar-app h2 {
  letter-spacing: -0.015em;
}
`.trim()
